<script setup lang="ts">
import { onMounted, onUnmounted, computed, ref, watch } from 'vue'
import { RouterView, RouterLink, useRoute, useRouter } from 'vue-router'
import { useEngraverStore } from '@/stores/engraver'
import { useAuthStore } from '@/stores/auth'
import { useAccountTabs } from '@/composables/useAccountTabs'
import NoEngraverWall from '@/components/onboarding/NoEngraverWall.vue'
import EulaWall from '@/components/eula/EulaWall.vue'
import DeviceSwitcher from '@/components/engraver/DeviceSwitcher.vue'
import SupportChat from '@/components/SupportChat.vue'
import { useSupportStore } from '@/stores/support'

const route = useRoute()
const authStore = useAuthStore()
const engraverStore = useEngraverStore()
const supportStore = useSupportStore()
const router = useRouter()

const useChrome = computed(() => {
  if (!authStore.isAuthenticated) return false
  return !(route.meta.standalone === true || route.meta.public === true)
})

// Server flag from /auth/me. Replaces RouterView with the no-engraver wall on
// every route except /setup itself — otherwise the customer can't reach the
// page that lets them resolve the wall. Belt-and-suspenders to the gateway,
// which already returns show_no_engraver_wall=false for fulfillment tenants:
// they never own an engraver, so the wall must never mount for them.
const showNoEngraverWall = computed(
  () => authStore.showNoEngraverWall && !authStore.isFulfillment && !route.path.startsWith('/setup'),
)

// The job on THIS user's selected engraver, if any. The bar used to show
// activeEngraves[0], which is fleet-wide: with two operators running two
// machines, one saw the other's percentage and the link dropped them into the
// other's job. Other people's runs still get a count, but never a link.
const myEngrave = computed(() => {
  const id = engraverStore.selectedEngraver?.engraver_id
  if (!id) return null
  const progress = engraverStore.progressByEngraver[id]
  return progress ? { id, progress } : null
})
const otherEngraveCount = computed(
  () => engraverStore.activeEngraves.length - (myEngrave.value ? 1 : 0),
)

// Server flag from /auth/me: the user has not accepted the current agreement.
// Takes precedence over the no-engraver wall. /setup is exempt because it
// carries its own acceptance step, and /account so someone who does not agree
// can still manage or cancel their account.
const showEulaWall = computed(
  () => authStore.eulaAcceptanceRequired && !route.path.startsWith('/setup') && route.path !== '/account',
)

// `firmware_outdated` is computed server-side against the current row in
// keepsake_engraver_firmware_releases. We only suppress here for the cases
// the server can't see: route-level (`/setup`) and offline engravers, where
// nagging would be wrong even if the version is stale.
const firmwareOutdated = computed(() => {
  const engraver = engraverStore.primaryEngraver
  if (!engraver) return false
  if (engraver.status === 'offline') return false
  if (route.path.startsWith('/setup')) return false
  return engraver.firmware_outdated
})

const latestFirmwareVersion = computed(
  () => engraverStore.primaryEngraver?.latest_firmware_version ?? null,
)

// Account sub-navigation, shared with AccountView so the entitlement gating
// (fulfillment tenants, tag-designer beta) can't drift between the two.
const { tabs: accountTabs, resolveTab } = useAccountTabs()
const activeAccountTab = computed(() => resolveTab(route.query.tab))

// Mobile nav panel. Below `md` the link row collapses behind a hamburger,
// leaving only the brand and the live engrave indicator on the bar itself —
// the row was already overflowing a 375px phone before Archive was added.
const mobileMenuOpen = ref(false)
function closeMobileMenu() {
  mobileMenuOpen.value = false
}
onMounted(() => document.addEventListener('click', closeMobileMenu))
onUnmounted(() => document.removeEventListener('click', closeMobileMenu))

// Refetch engraver data on navigation (catches firmware version changes after setup)
watch(() => route.path, () => {
  closeMobileMenu()
  if (authStore.isAuthenticated) engraverStore.fetchEngravers()
})

onMounted(async () => {
  if (authStore.token && !authStore.user) {
    await authStore.fetchMe()
  }
  if (authStore.isAuthenticated) {
    setTimeout(() => {
      engraverStore.fetchEngravers()
      engraverStore.connect()
      supportStore.refreshUnread()
      supportStore.connect()
    }, 2000)
  }
})

onUnmounted(() => {
  engraverStore.disconnect()
  supportStore.disconnect()
})

// Support lives in the nav menu and opens the chat panel. The reply email
// links to /?support=<conversation id>, which opens that conversation.
function openSupport() {
  closeMobileMenu()
  supportStore.openPanel()
}

watch(
  () => [route.query.support, authStore.isAuthenticated] as const,
  ([id, authed]) => {
    if (!authed || typeof id !== 'string' || !id) return
    supportStore.openPanel(id)
    const { support: _drop, ...rest } = route.query
    router.replace({ query: rest })
  },
  { immediate: true },
)

function logout() {
  engraverStore.disconnect()
  supportStore.reset()
  authStore.logout()
}
</script>

<template>
  <RouterView v-if="!useChrome" />
  <div v-else class="min-h-screen bg-gray-50">
    <!-- Navigation -->
    <nav class="relative bg-white border-b border-gray-200">
      <div class="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between h-16">
          <!-- The bar carries only the brand and live engraver state at every
               width; all navigation lives in the hamburger panel. -->
          <div class="flex items-center">
            <RouterLink to="/" class="text-xl font-bold text-gray-900">
              Keepsake
            </RouterLink>
          </div>
          <div class="flex items-center space-x-3 sm:space-x-4">
            <!-- Active-device selector (only when N > 1). Stays on the bar at
                 every width — which engraver is selected, and whether it's
                 online, is the one thing that must never need a tap to see. -->
            <DeviceSwitcher />
            <!-- Your own machine's job links straight to it, pinned to the
                 device so the engrave screen tracks the right run. Anyone
                 else's is a count only: it is not your job to open. -->
            <RouterLink v-if="myEngrave"
              :to="{ path: `/project/${myEngrave.progress.job_id}/engrave`,
                     query: { engraver: myEngrave.id } }"
              class="flex items-center space-x-2 text-sm hover:opacity-80 transition">
              <span class="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
              <span class="text-blue-600 font-medium">
                Engraving {{ myEngrave.progress.progress_pct.toFixed(0) }}%<span
                  v-if="otherEngraveCount > 0" class="text-gray-400 font-normal">
                  +{{ otherEngraveCount }}</span>
              </span>
            </RouterLink>
            <div v-else-if="otherEngraveCount > 0"
              class="flex items-center space-x-2 text-sm">
              <span class="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
              <span class="text-gray-500 whitespace-nowrap">
                {{ otherEngraveCount }} engraving
              </span>
            </div>
            <RouterLink
              v-else-if="engraverStore.engravers.length === 1 && engraverStore.selectedEngraver"
              :to="`/engraver/${engraverStore.selectedEngraver.engraver_id}`"
              class="flex items-center space-x-2 text-sm hover:opacity-80 transition"
            >
              <span
                class="w-2 h-2 rounded-full"
                :class="engraverStore.selectedEngraver.status !== 'offline' ? 'bg-green-500' : 'bg-red-500'"
              ></span>
              <span class="text-gray-500">
                {{ engraverStore.selectedEngraver.status || 'offline' }}
              </span>
            </RouterLink>
            <div v-else-if="!engraverStore.hasEngraver" class="flex items-center space-x-2 text-sm">
              <span class="w-2 h-2 rounded-full bg-red-500"></span>
              <span class="text-gray-500 whitespace-nowrap">No engraver</span>
            </div>
            <!-- Hamburger at every width. Stops propagation so the opening
                 click isn't swallowed by the document-level close handler. -->
            <button
              type="button"
              class="relative -mr-2 p-2 text-gray-600 hover:text-gray-900"
              @click.stop="mobileMenuOpen = !mobileMenuOpen"
              :aria-expanded="mobileMenuOpen"
              aria-controls="mobile-menu"
              aria-label="Toggle navigation menu"
            >
              <span v-if="supportStore.hasUnread && !mobileMenuOpen"
                class="absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white"
                aria-hidden="true" />
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path v-if="!mobileMenuOpen" stroke-linecap="round" stroke-linejoin="round"
                  stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                <path v-else stroke-linecap="round" stroke-linejoin="round"
                  stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Nav panel, every width. Overlays the page rather than pushing it
             down, so opening it doesn't shift the firmware banner and project
             grid. Full-bleed on phones; a right-aligned card from sm up, inset
             to match the container padding so it hangs off the hamburger.
             @click.stop keeps a tap inside it from closing it via the document
             handler. z-30 sits above content but under the support dialog. -->
        <div v-if="mobileMenuOpen" id="mobile-menu"
          class="absolute top-full z-30 left-0 right-0 sm:left-auto sm:right-6 lg:right-8 sm:w-72 sm:mt-1
                 bg-white border-b border-gray-200 sm:border sm:rounded-lg shadow-lg px-4 py-3 space-y-1"
          @click.stop>
        <RouterLink to="/" @click="closeMobileMenu"
          class="block px-2 py-2 rounded-md text-base text-gray-600 hover:bg-gray-50 hover:text-gray-900"
          :class="{ 'text-gray-900 font-medium bg-gray-50': route.path === '/' }">
          Projects
        </RouterLink>
        <RouterLink to="/archive" @click="closeMobileMenu"
          class="block px-2 py-2 rounded-md text-base text-gray-600 hover:bg-gray-50 hover:text-gray-900"
          :class="{ 'text-gray-900 font-medium bg-gray-50': route.path === '/archive' }">
          Archive
        </RouterLink>
        <!-- Account, with its sub-navigation nested underneath. The Account
             page no longer renders a tab bar of its own, so these are the only
             way to reach those panels — and they jump straight there from any
             page, not just from /account. -->
        <RouterLink to="/account" @click="closeMobileMenu"
          class="block px-2 py-2 rounded-md text-base text-gray-600 hover:bg-gray-50 hover:text-gray-900"
          :class="{ 'text-gray-900 font-medium bg-gray-50': route.path === '/account' }">
          Account
        </RouterLink>
        <div class="pl-3 ml-2 border-l border-gray-200 space-y-0.5">
          <RouterLink v-for="tab in accountTabs" :key="tab.id"
            :to="{ path: '/account', query: { tab: tab.id } }" @click="closeMobileMenu"
            class="block px-2 py-1.5 rounded-md text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-900"
            :class="{ 'text-gray-900 font-medium': route.path === '/account' && activeAccountTab === tab.id }">
            {{ tab.label }}
          </RouterLink>
        </div>
        <div class="pt-3 mt-2 border-t border-gray-200">
          <button type="button" @click="openSupport"
            class="flex items-center justify-between w-full text-left px-2 py-2 rounded-md text-base text-gray-600 hover:bg-gray-50 hover:text-gray-900">
            Support
            <span v-if="supportStore.hasUnread"
              class="ml-2 min-w-[1.25rem] px-1.5 py-0.5 rounded-full bg-red-500 text-white text-xs font-semibold text-center">
              {{ supportStore.unread }}
            </span>
          </button>
        </div>
        <div class="pt-3 mt-2 border-t border-gray-200">
          <div class="flex items-center justify-between">
            <span class="text-sm text-gray-700 truncate">{{ authStore.displayName }}</span>
            <button
              type="button"
              @click="logout"
              class="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-900 transition"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
      </div>
    </nav>

    <!-- Firmware update banner -->
    <div v-if="firmwareOutdated"
      class="bg-amber-50 border-b border-amber-200">
      <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
        <p class="text-sm text-amber-800">
          A firmware update is available for your engraver<span v-if="latestFirmwareVersion"> (v{{ latestFirmwareVersion }})</span>.
        </p>
        <RouterLink to="/setup/usb"
          class="text-sm font-medium text-amber-900 bg-amber-200 px-3 py-1 rounded-lg hover:bg-amber-300 transition whitespace-nowrap ml-4">
          Update Now
        </RouterLink>
      </div>
    </div>

    <!-- Main content -->
    <main class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <EulaWall v-if="showEulaWall" />
      <NoEngraverWall v-else-if="showNoEngraverWall" />
      <RouterView v-else />
    </main>

    <!-- Support chat, opened from the nav menu -->
    <SupportChat />
  </div>
</template>
